import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Loader2, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchCategories, submitContact } from "@/lib/api";
import { SITE } from "@/lib/site";

export default function ContactForm({ defaultProductSlug = "", defaultCategory = "" }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    category: defaultCategory,
    product_slug: defaultProductSlug,
    message: "",
  });

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      payload.name = form.name;
      payload.email = form.email;
      payload.message = form.message;
      await submitContact(payload);
      toast.success("Inquiry transmitted. Our team will respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", organization: "", category: "", product_slug: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-white border-slate-300 rounded-none h-12 text-[#0A1128] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 focus-visible:border-blue-600";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-3 text-red-600">// 05 · Direct Channel</div>
          <h2 className="font-display font-black uppercase text-[#0A1128] text-4xl md:text-5xl tracking-tighter leading-[0.95] mb-6">
            Open a <span className="text-red-600">secure line.</span>
          </h2>
          <p className="text-slate-600 text-base leading-relaxed mb-10 max-w-md">
            Submit your requirement — we typically respond within 24 hours with a detailed
            specification sheet, pricing and lead time.
          </p>

          <div className="space-y-px bg-slate-200 border border-slate-200 shadow-hard">
            {SITE.phones.map((p, i) => (
              <a
                key={p}
                href={`tel:${SITE.phonesRaw[i]}`}
                data-testid={`contact-phone-link-${i}`}
                className="flex items-center gap-4 bg-white p-5 lift hover:bg-slate-50"
              >
                <div className="w-11 h-11 border border-slate-300 flex items-center justify-center bg-slate-50">
                  <Phone className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <div className="overline">{i === 0 ? "Primary" : "Secondary"} · Direct Line</div>
                  <div className="font-display font-bold text-[#0A1128] text-base mt-1">{p}</div>
                </div>
              </a>
            ))}
            <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-4 bg-white p-5 lift hover:bg-slate-50">
              <div className="w-11 h-11 border border-slate-300 flex items-center justify-center bg-slate-50">
                <Mail className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="overline">Email</div>
                <div className="font-display font-bold text-[#0A1128] text-base mt-1 break-all">{SITE.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 bg-white p-5">
              <div className="w-11 h-11 border border-slate-300 flex items-center justify-center bg-slate-50">
                <Globe className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="overline">Website</div>
                <div className="font-display font-bold text-[#0A1128] text-base mt-1">{SITE.website}</div>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-5">
              <div className="w-11 h-11 border border-slate-300 flex items-center justify-center bg-slate-50 shrink-0">
                <MapPin className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <div className="overline">Headquarters</div>
                <div className="font-display font-bold text-[#0A1128] text-base mt-1">{SITE.address}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="relative border border-slate-200 bg-white p-6 md:p-10 corner-tl corner-tr corner-bl corner-br shadow-hard"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField label="Full Name *" htmlFor="name">
                <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Capt. R. Singh" className={inputCls} />
              </FormField>
              <FormField label="Email *" htmlFor="email">
                <Input id="email" data-testid="contact-email" type="email" value={form.email} onChange={update("email")} placeholder="ops@agency.gov.in" className={inputCls} />
              </FormField>
              <FormField label="Phone" htmlFor="phone">
                <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="+91" className={inputCls} />
              </FormField>
              <FormField label="Organization / Agency" htmlFor="organization">
                <Input id="organization" data-testid="contact-org" value={form.organization} onChange={update("organization")} placeholder="Department / Unit" className={inputCls} />
              </FormField>
              <FormField label="Equipment Category" htmlFor="category">
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger id="category" data-testid="contact-category" className="bg-white border-slate-300 rounded-none h-12 text-[#0A1128] focus:ring-2 focus:ring-blue-600 focus:ring-offset-0">
                    <SelectValue placeholder="Select a domain" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none text-[#0A1128]">
                    {cats.map((c) => (
                      <SelectItem key={c.slug} value={c.slug} className="rounded-none">
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Specific Product / Model (optional)" htmlFor="product_slug">
                <Input id="product_slug" data-testid="contact-product" value={form.product_slug} onChange={update("product_slug")} placeholder="e.g. MKC-H-500" className={inputCls} />
              </FormField>
            </div>

            <div className="mt-6">
              <FormField label="Mission Brief / Requirement *" htmlFor="message">
                <Textarea
                  id="message"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Quantity, delivery timeline, technical specifications…"
                  className="bg-white border-slate-300 rounded-none text-[#0A1128] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 focus-visible:border-blue-600"
                />
              </FormField>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="overline">// transmission encrypted · 24h response</p>
              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-none uppercase font-bold tracking-wide px-8 h-12"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {loading ? "Transmitting…" : "Submit Inquiry"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="overline">{label}</Label>
      {children}
    </div>
  );
}
